import { createContext, useContext, useEffect, useRef, useState } from "react";

const TOKEN_NAME = process.env.BASE_TOKEN_NAME;

const ChatSocketContext = createContext(null);

export function ChatSocketProvider({ children }) {
  const socketRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const listenersRef = useRef(new Set());
  const [lastEvent, setLastEvent] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  const accessToken =
    typeof window !== "undefined" && localStorage.getItem(TOKEN_NAME);

  useEffect(() => {
    if (!accessToken) return;

    const ws = new WebSocket(
      `${process.env.NEW_SOCKET_URL}/ws/realtime/?token=${accessToken}`
    );
    socketRef.current = ws;

    ws.onopen = () => {
      console.log("WS connected");
      setIsConnected(true);
      pingIntervalRef.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(
            JSON.stringify({
              event: "ping",
            })
          );
        }
      }, 30000);
    };
    ws.onclose = () => {
      console.log("WS disconnected");
      setIsConnected(false);
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
    ws.onerror = (err) => console.error("WS error", err);

    ws.onmessage = (rawEvent) => {
      try {
        const data = JSON.parse(rawEvent.data);
        console.log(data, "ws: Event triggered");

        // Update lastEvent for backward-compat consumers.
        setLastEvent(data);

        // Fire every listener synchronously — direct callbacks are not subject to React batching
        listenersRef.current.forEach((cb) => cb(data));
      } catch (e) {
        console.error("Error parsing WebSocket event:", e);
      }
    };

    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      ws.close();
    };
  }, [accessToken]);

  const send = (payload) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(payload));
      console.log(payload, "ws: send event payload");
    }
  };

  const onMessage = (cb) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  };

  const onEvent = (eventName, cb) =>
    onMessage((data) => {
      if (data?.event_name === eventName) cb(data);
    });

  const value = { send, onMessage, onEvent, socketRef, lastEvent, isConnected };

  return (
    <ChatSocketContext.Provider value={value}>
      {children}
    </ChatSocketContext.Provider>
  );
}

export function useChatSocket() {
  const ctx = useContext(ChatSocketContext);
  if (!ctx) {
    throw new Error("useChatSocket must be used inside <ChatSocketProvider>");
  }
  return ctx;
}